import {
  useCancelTracingAiSummary,
  useDismissAiProcessingItem,
} from '@hooks/useAiProcessingStats';
import { useRetryAuditProcessing } from '@hooks/useRetryAuditProcessing';
import {
  Badge,
  Button,
  Card,
  Flex,
  Progress,
  Text,
  Tooltip,
  useToast,
} from '@retracedgmbh/ui-components-lib';
import { useState } from 'react';
import { useQueryClient } from '@/hooks/useApi';
import useTranslator from '@/hooks/useTranslator';
import * as api from '@/services/api';
import {
  countAuditJobs,
  countAuditStages,
  deriveAuditStages,
  isCapImportContextMissing,
} from './auditStages';
import CancelAnalysisDialog from './CancelAnalysisDialog';
import StageRow from './StageRow';
import type { AiProcessingItem } from './types';

interface QueueCardProps {
  item: AiProcessingItem;
  index: number;
  section: 'inProgress' | 'failed' | 'review';
  onCta: (item: AiProcessingItem) => void;
}

const BULLET = ' • ';

// Feature-specific label for the flat COMPLETED review card so it's never empty.
// Static map (no dynamic i18n keys); reuses the existing per-type "ready" labels.
// Unmapped types fall back to the generic `itemCard.reviewReady`.
const READY_LABEL_KEYS: Record<string, string> = {
  AUDIT: 'popover.readyType.audit',
  CERTIFICATE: 'popover.readyType.certificate',
  TRACING: 'popover.readyType.tracing',
  CERTIFIED_MATERIALS: 'popover.readyType.certifiedMaterials',
  COMPANY_DOCUMENTS: 'popover.readyType.companyDocuments',
};

export default function QueueCard({
  item,
  index,
  section,
  onCta,
}: QueueCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dismissMutation = useDismissAiProcessingItem();
  const cancelMutation = useCancelTracingAiSummary();
  const retryMutation = useRetryAuditProcessing();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { getTranslation } = useTranslator({ page: 'ai-processing' });

  const isProcessing = item.status === 'PROCESSING';
  const isCompleted = item.status === 'COMPLETED';
  const isFailed = item.status === 'FAILED';
  // `metadata` is typed `Record<string, string>` but carries a nested `file`
  // object at runtime — cast through `unknown` like ActiveJobsPopover does.
  const metadataFile = (item.metadata as Record<string, unknown> | null)
    ?.file as { name?: string } | undefined;
  const fileName = item.assetName || metadataFile?.name;

  // Audit items carry a backend per-step breakdown (`item.steps`) → one row per
  // visible stage (see auditStages.ts). This includes FAILED audits: their stages
  // render too (finished stages COMPLETED + the stopped stage as a red error row
  // with Retry), so an audit only falls back to the flat body when it has no steps
  // yet. Non-audit types have no steps → flat single-job body.
  const isAudit = item.assetType === 'AUDIT';
  const stages = isAudit ? deriveAuditStages(item.steps) : [];
  const hasStages = stages.length > 0;
  // Chronology gate: the extracted audit details must be reviewed before any
  // other review job on the same card. A reviewed audit-details stage renders
  // COMPLETED; while it's still a REVIEW row, every OTHER review row is disabled
  // (with an explanatory tooltip). The audit-details row itself is never gated.
  const auditDetailsStage = stages.find(stage => stage.id === 'auditDetails');
  const auditDetailsReviewed =
    !auditDetailsStage || auditDetailsStage.status === 'COMPLETED';
  // A CAP-only import whose queue row never recorded the audit + company it
  // belongs to has no review to open (those are chosen before the analysis starts
  // and are not re-decidable at review time), so its CAP stage's Review CTA is
  // disabled with an explanatory tooltip. The card keeps its dismiss X.
  const capImportContextMissing = isCapImportContextMissing(item);
  // Backend failure message for the failed stage's error row (StageRow falls back
  // to a localized string when this is absent).
  const errorMessage = (
    (item.metadata as Record<string, unknown> | null)?.error as
      | { message?: string }
      | undefined
  )?.message;
  // A failed card keeps a dark check in the header when an earlier stage finished;
  // only a fully-failed card (nothing finished) shows the red triangle.
  const failedHasProgress =
    isFailed &&
    stages.some(s => s.status === 'COMPLETED' || s.status === 'REVIEW');
  // Feature-specific label for the flat COMPLETED review card so it's never
  // empty; unmapped types fall back to the generic "Ready to review".
  const readyLabelKey = READY_LABEL_KEYS[item.assetType];
  const readyLabel = readyLabelKey
    ? getTranslation(readyLabelKey)
    : getTranslation('itemCard.reviewReady');

  // running/completed/ready come from the grouped stages; the "N jobs" total is
  // the real backend step count (incl. pending, excl. internal EXTRACTION) so a
  // card that folds steps into fewer rows still reports the true total.
  const counts = hasStages
    ? { ...countAuditStages(stages), jobs: countAuditJobs(item.steps) }
    : {
        jobs: 1,
        running: isProcessing ? 1 : 0,
        completed: 0,
        readyToReview: isCompleted ? 1 : 0,
      };

  // Dismiss/cancel-X visibility. Completed jobs show the X (dismiss) per the
  // redesign + AC ("users can dismiss a completed or failed job") — including
  // completed certificates, whose X is the way out when the review's create
  // fails (e.g. a duplicate certificate). Only in-progress tracing hides it.
  const isTracingProcessing = item.assetType === 'TRACING' && isProcessing;
  // A stuck tracing analysis can only be cancelled after 1 hour (the backend
  // enforces this too). The widget auto-refetches every 10s while processing.
  const TRACING_CANCEL_MIN_MS = 60 * 60 * 1000;
  const tracingCancelReady =
    isTracingProcessing &&
    !!item.startedAt &&
    Date.now() - new Date(item.startedAt).getTime() >= TRACING_CANCEL_MIN_MS;
  const showDismiss = !isTracingProcessing || tracingCancelReady;
  const tooltipLabel = isProcessing
    ? getTranslation('itemCard.cancelJob')
    : getTranslation('itemCard.dismiss');

  const handleDismiss = async () => {
    try {
      if (item.assetType === 'AUDIT' && isProcessing) {
        await api.backend.cancelCapaWorkflow({ workflowId: item.assetId });
      }
      if (item.assetType === 'CERTIFICATE' && isProcessing) {
        await api.backend.cancelCertificateAiWorkflow({
          workflowId: item.assetId,
        });
      }
      if (item.assetType === 'TRACING' && isProcessing) {
        // Cancel the whole tracing summary (all in-flight types). Resolved by
        // tracingId + traceType from the processing-status metadata (metadata
        // uses `traceType` from create, `tracingType` from result writeback).
        const metadata = item.metadata as Record<string, string> | null;
        const tracingId = metadata?.tracingId;
        const traceType = metadata?.traceType ?? metadata?.tracingType;
        if (tracingId && traceType) {
          await cancelMutation.mutateAsync({ tracingId, traceType });
        }
      }
      await dismissMutation.mutateAsync(item.id);
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
    } catch (error) {
      toast({ title: (error as Error).message, variant: 'destructive' });
    }
  };

  // Every dismiss (any status / asset type) asks for confirmation first — the
  // alert warns that an in-progress process will be stopped. Confirm →
  // handleDismiss, which also cancels the running workflow via the per-type
  // endpoint (audit / certificate / tracing) before removing the row.
  const requestDismiss = () => setConfirmOpen(true);

  // Retry a failed AUDIT workflow (audit-only — the hook re-runs `processCapa`
  // with the failed workflow's file ids, then dismisses the stale row). Other
  // asset types have no retry, so their failed rows render no Retry button.
  const handleRetry = async () => {
    try {
      await retryMutation.mutateAsync({
        workflowId: item.assetId,
        itemId: item.id,
      });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
    } catch (error) {
      toast({ title: (error as Error).message, variant: 'destructive' });
    }
  };

  // Header status badge: section + status driven. Processing = brand
  // ('primary'); failed = red; ready-for-review = amber. Green ('secondary') is
  // reserved for the "Completed" state only.
  // Badges never shrink/wrap (`shrink-0` + `whitespace-nowrap`) — the long file
  // name truncates instead of squeezing them (see the header `Text` below).
  const headerBadge =
    section === 'review' ? (
      <Badge
        radius='squared'
        type='warning'
        className='tw:shrink-0 tw:whitespace-nowrap'
      >
        <Text className='tw:text-amber-800'>
          {getTranslation('queue.readyForReview', {
            count: counts.readyToReview,
          })}
        </Text>
      </Badge>
    ) : section === 'failed' ? (
      <Badge
        radius='squared'
        type='destructive'
        className='tw:shrink-0 tw:whitespace-nowrap'
      >
        {getTranslation('itemCard.status.failed')}
      </Badge>
    ) : (
      <Badge
        radius='squared'
        type='primary'
        className='tw:shrink-0 tw:whitespace-nowrap'
      >
        {getTranslation('itemCard.status.processing')}
      </Badge>
    );

  // Sub-line: counts differ in shape between the two sections (per the design).
  const subline =
    section === 'inProgress'
      ? [
          getTranslation('subline.running', { count: counts.running }),
          getTranslation('subline.completed', { count: counts.completed }),
          getTranslation('subline.jobs', { count: counts.jobs }),
          getTranslation('subline.readyToReview', {
            count: counts.readyToReview,
          }),
        ].join(BULLET)
      : [
          getTranslation('subline.files', { count: 1 }),
          getTranslation('subline.jobs', { count: counts.jobs }),
          getTranslation('subline.readyToReview', {
            count: counts.readyToReview,
          }),
        ].join(BULLET);

  return (
    <>
      <Card variant='outline' className='tw:p-lg'>
        <Flex direction='column' gap='md'>
          {/* Header */}
          <Flex direction='column' gap='xxs'>
            <Flex justify='between' alignItems='center'>
              <Flex alignItems='center' gap='sm' className='tw:min-w-0'>
                <Text size='small' weight='semibold' className='tw:shrink-0'>
                  {index}.
                </Text>
                <i
                  className={
                    section === 'review'
                      ? 'fa-regular fa-circle-check tw:text-content-brand tw:shrink-0'
                      : section === 'failed'
                        ? failedHasProgress
                          ? 'fa-regular fa-circle-check tw:text-content-primary tw:shrink-0'
                          : 'fa-regular fa-triangle-exclamation tw:text-content-negative tw:shrink-0'
                        : 'fa-regular fa-file tw:text-content-secondary tw:shrink-0'
                  }
                />
                {/* File name truncates (min-w-0 lets it shrink in the flex row)
                    so a long name never overflows or squeezes the badge/X. */}
                <Text
                  size='small'
                  weight='semibold'
                  textOverFlow='truncate'
                  className='tw:min-w-0'
                >
                  {fileName}
                </Text>
                {headerBadge}
              </Flex>
              {showDismiss && (
                <Tooltip title={tooltipLabel} placement='top'>
                  <Button variant='ghost' size='icon' onClick={requestDismiss}>
                    <i className='fa-regular fa-xmark' />
                  </Button>
                </Tooltip>
              )}
            </Flex>
            <Text size='small' color='secondary'>
              {subline}
            </Text>
          </Flex>

          {/* Body — audit stage rows when the backend provides `item.steps`;
              otherwise a flat single-job body. */}
          {hasStages ? (
            <Flex direction='column' gap='sm'>
              {stages.map(stage => (
                <StageRow
                  key={stage.id}
                  stage={stage}
                  onReview={() => onCta(item)}
                  reviewDisabledReason={
                    capImportContextMissing && stage.id === 'capExtraction'
                      ? 'capImportContext'
                      : stage.id !== 'auditDetails' &&
                          stage.status === 'REVIEW' &&
                          !auditDetailsReviewed
                        ? 'auditDetailsFirst'
                        : undefined
                  }
                  errorMessage={errorMessage}
                  canRetry={isAudit}
                  onRetry={handleRetry}
                  onDismiss={requestDismiss}
                  isRetrying={retryMutation.isPending}
                />
              ))}
            </Flex>
          ) : isProcessing ? (
            <Flex alignItems='center' gap='sm'>
              <Flex className='tw:flex-1'>
                <Progress className='tw:h-1' value={item.percentage} />
              </Flex>
              <Text
                size='small'
                weight='semibold'
                className='tw:text-content-brand tw:whitespace-nowrap'
              >
                {getTranslation('itemCard.percentComplete', {
                  percentage: item.percentage,
                })}
              </Text>
            </Flex>
          ) : isFailed ? (
            <Flex justify='end'>
              <Button
                variant='fill-danger'
                size='sm'
                onClick={() => onCta(item)}
              >
                {getTranslation('itemCard.viewError')}
              </Button>
            </Flex>
          ) : (
            // Completed with no stage breakdown (non-audit, or an audit with no
            // steps yet): a single amber review row carrying a feature-specific
            // label (generic fallback otherwise) so it's never empty.
            <Card
              variant='outline'
              className='tw:p-md tw:bg-background-notice-primary-subtle'
            >
              <Flex justify='between' alignItems='center' gap='sm'>
                <Flex alignItems='center' gap='sm' className='tw:min-w-0'>
                  <i className='fa-regular fa-circle-check tw:text-content-notice-primary tw:shrink-0' />
                  <Text size='small' weight='semibold' textOverFlow='truncate'>
                    {readyLabel}
                  </Text>
                </Flex>
                <Button
                  onClick={() => onCta(item)}
                  icon='fa-regular fa-chevron-right'
                  iconPosition='right'
                  className='tw:shrink-0'
                >
                  {getTranslation('itemCard.review')}
                </Button>
              </Flex>
            </Card>
          )}
        </Flex>
      </Card>

      <CancelAnalysisDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        assetName={item.assetName}
        onConfirm={handleDismiss}
        variant='dismiss'
      />
    </>
  );
}
